import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useSecuritySettings } from '@/hooks/useSecuritySettings';
import { useInactivityTimeoutStore } from '@/store/inactivityTimeout';
import {
  getOrCreateSessionToken,
  getSessionId,
  getStoredDeviceInfo,
  parseUserAgent,
  setSessionId,
  setStoredDeviceInfo,
} from '@/lib/sessionUtils';
import { logSessionAuditEvent } from '@/lib/sessionAudit';

// Constants
const WARNING_SECONDS = 60;
const CHECK_INTERVAL_MS = 10000;
const THROTTLE_MS = 1000;
const ACTIVITY_UPDATE_THROTTLE_MS = 60000;
const HEARTBEAT_INTERVAL_MS = 30 * 60 * 1000;
const BACKGROUND_SESSION_SYNC_INTERVAL_MS = 60 * 1000;
const STORAGE_KEY = 'blazepos-last-activity';

interface InactivityContextType {
  resetActivity: () => void;
}

const InactivityContext = createContext<InactivityContextType | undefined>(undefined);

export function InactivityProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, signOut, user, profile } = useAuth();
  const { securitySettings } = useSecuritySettings();
  const store = useInactivityTimeoutStore();

  const lastActivityRef = useRef<number>(Date.now());
  const lastThrottleRef = useRef<number>(0);
  const lastActivityUpdateRef = useRef<number>(0);
  const lastHeartbeatRef = useRef<number>(0);
  const lastKnownIpRef = useRef<string | null>(null);
  const timeoutReasonRef = useRef<'inactivity' | 'session_timeout'>('session_timeout');
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getTimeoutConfig = useCallback(() => {
    const timeouts: Array<{ ms: number; reason: 'inactivity' | 'session_timeout' }> = [];
    const minutes = securitySettings.inactivity_timeout_minutes;
    const hours = securitySettings.session_timeout_hours;

    if (minutes && securitySettings.require_reauth_on_inactivity) {
      timeouts.push({ ms: minutes * 60 * 1000, reason: 'inactivity' });
    }

    if (hours && hours > 0) {
      timeouts.push({ ms: hours * 60 * 60 * 1000, reason: 'session_timeout' });
    }

    if (timeouts.length === 0) {
      return null;
    }

    const shortest = timeouts.sort((a, b) => a.ms - b.ms)[0];
    return shortest;
  }, [
    securitySettings.inactivity_timeout_minutes,
    securitySettings.require_reauth_on_inactivity,
    securitySettings.session_timeout_hours,
  ]);

  // Reset activity timestamp
  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    // Store in localStorage for cross-tab sync
    try {
      localStorage.setItem(STORAGE_KEY, now.toString());
    } catch {
      // Ignore storage errors
    }

    // If warning is visible, hide it
    if (store.isWarningVisible) {
      store.resetWarning();
    }
  }, [store]);

  // Throttled activity handler
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottleRef.current < THROTTLE_MS) {
      return;
    }
    lastThrottleRef.current = now;
    resetActivity();
  }, [resetActivity]);

  const ensureSessionRecord = useCallback(async () => {
    if (!user || !profile?.organization_id) return;

    const sessionToken = getOrCreateSessionToken();
    const storedSessionId = getSessionId();
    if (storedSessionId) return;

    const { data: existing } = await supabase
      .from('user_sessions')
      .select('id, ip_address')
      .eq('session_token', sessionToken)
      .maybeSingle();

    if (existing?.id) {
      setSessionId(existing.id);
      lastKnownIpRef.current = existing.ip_address ?? null;
      return;
    }

    const deviceInfo = parseUserAgent(navigator.userAgent);
    const { data: sessionId } = await supabase.rpc('create_user_session', {
      p_user_id: user.id,
      p_organization_id: profile.organization_id,
      p_session_token: sessionToken,
      p_user_agent: navigator.userAgent,
      p_device_type: deviceInfo.deviceType,
      p_browser: deviceInfo.browser,
      p_browser_version: deviceInfo.browserVersion,
      p_os: deviceInfo.os,
      p_os_version: deviceInfo.osVersion,
    });

    if (sessionId) {
      setSessionId(sessionId as string);
    }
  }, [profile?.organization_id, user]);

  const maybeUpdateSessionActivity = useCallback(async () => {
    if (!user) return;

    const now = Date.now();
    if (now - lastActivityUpdateRef.current < ACTIVITY_UPDATE_THROTTLE_MS) {
      return;
    }

    lastActivityUpdateRef.current = now;
    const sessionToken = getOrCreateSessionToken();
    await supabase.rpc('update_user_session_activity', {
      p_session_token: sessionToken,
    });
  }, [user]);

  const maybeLogHeartbeat = useCallback(async () => {
    if (!user) return;

    const now = Date.now();
    if (now - lastHeartbeatRef.current < HEARTBEAT_INTERVAL_MS) {
      return;
    }

    lastHeartbeatRef.current = now;
    const sessionToken = getOrCreateSessionToken();
    const sessionId = getSessionId();

    await logSessionAuditEvent({
      userId: user.id,
      sessionId,
      sessionToken,
      action: 'session_heartbeat',
      isSuperadmin: profile?.role === 'superadmin',
      metadata: {
        session_token: sessionToken,
        last_activity_at: new Date(lastActivityRef.current).toISOString(),
      },
    });

    if (sessionId) {
      const { data: sessionRow } = await supabase
        .from('user_sessions')
        .select('ip_address')
        .eq('id', sessionId)
        .maybeSingle();

      const currentIp = sessionRow?.ip_address ?? null;
      if (currentIp && currentIp !== lastKnownIpRef.current) {
        await logSessionAuditEvent({
          userId: user.id,
          sessionId,
          sessionToken,
          action: 'session_ip_change',
          isSuperadmin: profile?.role === 'superadmin',
          metadata: {
            from: lastKnownIpRef.current,
            to: currentIp,
            session_token: sessionToken,
          },
        });
      }

      lastKnownIpRef.current = currentIp;
    }
  }, [profile?.role, user]);

  const maybeLogDeviceChange = useCallback(async () => {
    if (!user) return;

    const stored = getStoredDeviceInfo();
    const current = {
      userAgent: navigator.userAgent,
      ...parseUserAgent(navigator.userAgent),
    };
    const sessionToken = getOrCreateSessionToken();

    if (!stored) {
      setStoredDeviceInfo(current);
      return;
    }

    if (
      stored.userAgent === current.userAgent &&
      stored.browser === current.browser &&
      stored.browserVersion === current.browserVersion &&
      stored.os === current.os &&
      stored.osVersion === current.osVersion &&
      stored.deviceType === current.deviceType
    ) {
      return;
    }

    setStoredDeviceInfo(current);

    await logSessionAuditEvent({
      userId: user.id,
      sessionId: getSessionId(),
      sessionToken,
      action: 'session_device_change',
      isSuperadmin: profile?.role === 'superadmin',
      metadata: {
        from: stored,
        to: current,
      },
    });

    if (user && profile?.organization_id) {
      const { data: sessionId } = await supabase.rpc('create_user_session', {
        p_user_id: user.id,
        p_organization_id: profile.organization_id,
        p_session_token: sessionToken,
        p_user_agent: navigator.userAgent,
        p_device_type: current.deviceType,
        p_browser: current.browser,
        p_browser_version: current.browserVersion,
        p_os: current.os,
        p_os_version: current.osVersion,
      });
      if (sessionId) {
        setSessionId(sessionId as string);
      }
    }
  }, [profile?.organization_id, profile?.role, user]);

  // Handle auto-logout
  const handleAutoLogout = useCallback(async (reason: 'inactivity' | 'session_timeout') => {
    // Clear intervals
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    // Reset store
    store.resetWarning();

    // Sign out
    try {
      await signOut(reason === 'inactivity' ? 'inactivity' : 'session_timeout');
    } catch (error) {
      console.error('Auto-logout failed:', error);
    }
  }, [signOut, store]);

  // Start countdown when warning becomes visible
  useEffect(() => {
    if (!store.isWarningVisible) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      const remaining = store.secondsRemaining - 1;
      if (remaining <= 0) {
        handleAutoLogout(timeoutReasonRef.current);
      } else {
        store.setSecondsRemaining(remaining);
      }
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [store.isWarningVisible, store.secondsRemaining, handleAutoLogout, store]);

  // Check inactivity at regular intervals
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const timeoutConfig = getTimeoutConfig();
    if (!timeoutConfig) {
      return;
    }

    const checkInactivity = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      const warningThreshold = timeoutConfig.ms - (WARNING_SECONDS * 1000);

      if (elapsed >= timeoutConfig.ms) {
        timeoutReasonRef.current = timeoutConfig.reason;
        handleAutoLogout(timeoutConfig.reason);
        return;
      }

      if (elapsed >= warningThreshold && !store.isWarningVisible) {
        // Show warning
        timeoutReasonRef.current = timeoutConfig.reason;
        store.setWarningVisible(true);
        store.setSecondsRemaining(WARNING_SECONDS);
      }
    };

    // Initial check
    checkInactivity();

    // Set up interval
    checkIntervalRef.current = setInterval(checkInactivity, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [getTimeoutConfig, handleAutoLogout, isAuthenticated, store]);

  // Activity tracking is wired up in the session tracking effect below.

  // Cross-tab synchronization via localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const timestamp = parseInt(e.newValue, 10);
        if (!isNaN(timestamp)) {
          lastActivityRef.current = timestamp;
          // If warning is visible but activity happened in another tab, reset
          if (store.isWarningVisible) {
            store.resetWarning();
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [store]);

  // Initialize activity timestamp on mount
  useEffect(() => {
    if (isAuthenticated) {
      resetActivity();
      const syncSession = async () => {
        await ensureSessionRecord();
        await maybeLogDeviceChange();
      };
      syncSession();
    }
  }, [ensureSessionRecord, isAuthenticated, maybeLogDeviceChange, resetActivity]);

  // Clean up on settings change (timeout disabled)
  useEffect(() => {
    const timeoutConfig = getTimeoutConfig();
    if (!timeoutConfig && store.isWarningVisible) {
      store.resetWarning();
    }
  }, [getTimeoutConfig, store]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const runActivityUpdate = async () => {
      await maybeUpdateSessionActivity();
      await maybeLogHeartbeat();
    };

    runActivityUpdate();
  }, [isAuthenticated, maybeLogHeartbeat, maybeUpdateSessionActivity]);

  // Keep long-lived tabs represented as active sessions and emit periodic heartbeats.
  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      const syncSession = async () => {
        await maybeUpdateSessionActivity();
        await maybeLogHeartbeat();
      };
      syncSession();
    }, BACKGROUND_SESSION_SYNC_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isAuthenticated, maybeLogHeartbeat, maybeUpdateSessionActivity]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivityWithTracking = () => {
      handleActivity();
      maybeUpdateSessionActivity();
      maybeLogHeartbeat();
    };

    const events: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleActivityWithTracking, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivityWithTracking);
      });
    };
  }, [handleActivity, isAuthenticated, maybeLogHeartbeat, maybeUpdateSessionActivity]);

  return (
    <InactivityContext.Provider value={{ resetActivity }}>
      {children}
    </InactivityContext.Provider>
  );
}

export function useInactivity() {
  const context = useContext(InactivityContext);
  if (context === undefined) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  return context;
}
